import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import * as XLSX from 'xlsx';
import { DateLocalePipe } from '../../../../../../../../aumico-lib-core-fe/src/lib';
import { GetUserDto } from '../../../../../../../../aumico-lib-users-fe/src/lib';
import { FcpTreeItem } from '../models/fcpTreeItem.model';

// Export selection structure
export type ExportSelection = {
    unlabeledComments: boolean;
    internalComments: boolean;
    externalComments: boolean;
    reviewComments: boolean;
    pendencies: boolean;
    closing: boolean;
    exportExcel: boolean;
};

// DTO for export options
export type ExportOptionsDTO = {
    unlabeledComments: boolean;
    internalComments: boolean;
    externalComments: boolean;
    pendencies: boolean;
    closing: boolean;
    reviewComments: boolean;
};

// General data structure interface (added for the general data in JSON export)
export interface GeneralDataDTO {
    arName: string;
    arAddress: string;
    arAddressSupplement: string;
    arZip: string;
    arCity: string;
    organizationName: string;
    closingDate: string;
    creationDate: string;
    creatorName: string;
}

// Main data structure for JSON export
export interface FcpDataDTO {
    groupedcomments: GroupedCommentDTO[];
    closingentries: ClosingEntryDTO[];
    options: ExportOptionsDTO;
    metadata: ExportOptionsDTO; // Duplicate of options for metadata purposes
    generaldata: GeneralDataDTO; // General data (metadata for the export)
}

// Grouped comment DTO, grouping comments by some criteria (e.g., FCP TreeItem)
export interface GroupedCommentDTO {
    group: string; // The group name (e.g., item name)
    comments: CommentDTO[]; // Array of comments under this group
}

// Comment DTO, representing individual comments with their attributes
export interface CommentDTO {
    dueDate: string; // Due date for the comment
    text: string; // The comment text
    createdByName: string; // The name of the person who created the comment
    isPendency: boolean; // True if the comment represents a pendency
    isInternal: boolean; // True if the comment is internal only
    isExternal: boolean; // True if the comment is external
    needsReview: boolean; // True if the comment requires review
    creationDate: string; // The creation date of the comment
}

// DTO for closing entries in JSON export
export interface ClosingEntryDTO {
    dueDate: string; // Due date for the closing entry
    text: string; // The description or text of the closing entry
    amount: number; // Amount involved in the entry
    debit: string; // Debit account identifier
    credit: string; // Credit account identifier
    debitName: string; // Debit account identifier
    creditName: string; // Credit account identifier
    booked: boolean; // Whether the entry is booked or not
}

export class FcpExportHelper {
    constructor(private readonly dateLocalPipe: DateLocalePipe) {}

    public export(
        selection: ExportSelection,
        items: FcpTreeItem[],
        dueDate: Date,
        annualReport: IAnnualReport,
        authoringCompanyName: string,
        user: GetUserDto,
    ): boolean | FcpDataDTO {
        if (selection.exportExcel) {
            return this.exportToExcel(selection, items, dueDate, annualReport.generalInformation.company.name);
        } else {
            return this.exportToJSON(selection, items, dueDate, annualReport, authoringCompanyName, user);
        }
    }

    private exportToExcel(
        selection: ExportSelection,
        items: FcpTreeItem[],
        dueDate: Date,
        companyName: string,
    ): boolean {
        const workbook = XLSX.utils.book_new();

        // Export pendencies
        if (selection.pendencies) {
            const pendencies = this.collectPendencies(items, dueDate);
            const worksheetPendencies = XLSX.utils.json_to_sheet(pendencies);
            XLSX.utils.book_append_sheet(workbook, worksheetPendencies, 'Pendencies');
        }

        // Export internal comments
        if (selection.internalComments) {
            const internalComments = this.collectInternalComments(items, dueDate);
            const worksheetInternalComments = XLSX.utils.json_to_sheet(internalComments);
            XLSX.utils.book_append_sheet(workbook, worksheetInternalComments, 'Internal Comments');
        }

        // Export external comments
        if (selection.externalComments) {
            const externalComments = this.collectExternalComments(items, dueDate);
            const worksheetExternalComments = XLSX.utils.json_to_sheet(externalComments);
            XLSX.utils.book_append_sheet(workbook, worksheetExternalComments, 'External Comments');
        }
        // Export external comments
        if (selection.reviewComments) {
            const reviewComments = this.collectReviewComments(items, dueDate);
            const worksheetReviewComments = XLSX.utils.json_to_sheet(reviewComments);
            XLSX.utils.book_append_sheet(workbook, worksheetReviewComments, 'Review Comments');
        }

        // Export unlabeled comments
        if (selection.unlabeledComments) {
            const unlabeledComments = this.collectUnlabeledComments(items, dueDate);
            const worksheetExternalComments = XLSX.utils.json_to_sheet(unlabeledComments);
            XLSX.utils.book_append_sheet(workbook, worksheetExternalComments, 'Unlabeled Comments');
        }

        // Export closing entries
        if (selection.closing) {
            const closingEntries = this.collectClosingEntries(items, dueDate);
            const worksheetClosing = XLSX.utils.json_to_sheet(closingEntries);
            XLSX.utils.book_append_sheet(workbook, worksheetClosing, 'Closing Entries');
        }

        const fileName = companyName + ', ' + this.dateLocalPipe.transform(dueDate)?.toString() + '.xlsx';

        // Write the workbook to a file
        XLSX.writeFile(workbook, fileName);

        return true;
    }

    // Method to collect pendencies
    private collectPendencies(items: FcpTreeItem[], dueDate: Date): { createdAt: Date; text: string }[] {
        let pendencies: { createdAt: Date; text: string }[] = [];

        items.forEach((item) => {
            const itemPendencies = item.comments
                .filter((comment) => comment.isPendency)
                .filter((comment) => new Date(comment.dueDate) >= dueDate)
                .map((comment) => ({ createdAt: new Date(comment.createdAt), text: comment.text }));

            pendencies = pendencies.concat(itemPendencies);

            if (item.children && item.children.length > 0) {
                pendencies = pendencies.concat(this.collectPendencies(item.children, dueDate));
            }
        });

        return pendencies;
    }

    // Method to collect internal comments
    private collectInternalComments(items: FcpTreeItem[], dueDate: Date): { createdAt: Date; text: string }[] {
        let internalComments: { createdAt: Date; text: string }[] = [];

        items.forEach((item) => {
            const itemInternalComments = item.comments
                .filter((comment) => comment.isInternal)
                .filter((comment) => new Date(comment.dueDate) >= dueDate)
                .map((comment) => ({ createdAt: new Date(comment.createdAt), text: comment.text }));

            internalComments = internalComments.concat(itemInternalComments);

            if (item.children && item.children.length > 0) {
                internalComments = internalComments.concat(this.collectInternalComments(item.children, dueDate));
            }
        });

        return internalComments;
    }

    // Method to collect external comments
    private collectExternalComments(items: FcpTreeItem[], dueDate: Date): { createdAt: Date; text: string }[] {
        let externalComments: { createdAt: Date; text: string }[] = [];

        items.forEach((item) => {
            const itemExternalComments = item.comments
                .filter((comment) => comment.isExternal)
                .filter((comment) => new Date(comment.dueDate) >= dueDate)
                .map((comment) => ({ createdAt: new Date(comment.createdAt), text: comment.text }));

            externalComments = externalComments.concat(itemExternalComments);

            if (item.children && item.children.length > 0) {
                externalComments = externalComments.concat(this.collectExternalComments(item.children, dueDate));
            }
        });

        return externalComments;
    }
    // Method to collect review comments
    private collectReviewComments(items: FcpTreeItem[], dueDate: Date): { createdAt: Date; text: string }[] {
        let reviewComments: { createdAt: Date; text: string }[] = [];

        items.forEach((item) => {
            const itemReviewComment = item.comments
                .filter((comment) => comment.needsReview)
                .filter((comment) => new Date(comment.dueDate) >= dueDate)
                .map((comment) => ({ createdAt: new Date(comment.createdAt), text: comment.text }));

            reviewComments = reviewComments.concat(itemReviewComment);

            if (item.children && item.children.length > 0) {
                reviewComments = reviewComments.concat(this.collectReviewComments(item.children, dueDate));
            }
        });

        return reviewComments;
    }

    // Method to collect unlabeled comments
    private collectUnlabeledComments(items: FcpTreeItem[], dueDate: Date): { createdAt: Date; text: string }[] {
        let unlabeledComments: { createdAt: Date; text: string }[] = [];

        items.forEach((item) => {
            const itemUnlabeledComments = item.comments
                .filter(
                    (comment) =>
                        !comment.isExternal && !comment.isInternal && !comment.isPendency && !comment.needsReview,
                )
                .filter((comment) => new Date(comment.dueDate) >= dueDate)
                .map((comment) => ({ createdAt: new Date(comment.createdAt), text: comment.text }));

            unlabeledComments = unlabeledComments.concat(itemUnlabeledComments);

            if (item.children && item.children.length > 0) {
                unlabeledComments = unlabeledComments.concat(this.collectUnlabeledComments(item.children, dueDate));
            }
        });

        return unlabeledComments;
    }

    // JSON export logic
    private exportToJSON(
        selection: ExportSelection,
        items: FcpTreeItem[],
        dueDate: Date,
        annualReport: IAnnualReport,
        authoringCompanyName: string,
        user: GetUserDto,
    ): FcpDataDTO {
        const groupedComments: GroupedCommentDTO[] = [];
        const closingEntries: ClosingEntryDTO[] = [];

        // Collect comments based on selection and group them
        if (selection.internalComments || selection.externalComments || selection.pendencies) {
            const comments = this.collectAllComments(items, dueDate, selection);
            const grouped = this.groupCommentsByItemName(comments, items);
            groupedComments.push(...grouped);
        }

        // Collect closing entries
        if (selection.closing) {
            const entries = this.collectClosingEntries(items, dueDate);
            closingEntries.push(...entries);
        }

        // Prepare options for export
        const options: ExportOptionsDTO = {
            unlabeledComments: selection.unlabeledComments,
            internalComments: selection.internalComments,
            externalComments: selection.externalComments,
            pendencies: selection.pendencies,
            closing: selection.closing,
            reviewComments: selection.reviewComments,
        };
        // Prepare meta data for export
        const meta: ExportOptionsDTO = {
            unlabeledComments: groupedComments.some((group) =>
                group.comments.some(
                    (comment) =>
                        !comment.isInternal && !comment.isExternal && !comment.isPendency && !comment.needsReview,
                ),
            ),
            internalComments: groupedComments.some((group) => group.comments.some((comment) => comment.isInternal)),
            externalComments: groupedComments.some((group) => group.comments.some((comment) => comment.isExternal)),
            pendencies: groupedComments.some((group) => group.comments.some((comment) => comment.isPendency)),
            closing: closingEntries.length > 0,
            reviewComments: groupedComments.some((group) => group.comments.some((comment) => comment.needsReview)),
        };

        // Create general data for export
        const generaldata: GeneralDataDTO = {
            arName: annualReport.generalInformation.company.name,
            arAddress: annualReport.generalInformation.company.street,
            arAddressSupplement: annualReport.generalInformation.company.additionToAddress,
            arZip: annualReport.generalInformation.company.zip,
            arCity: annualReport.generalInformation.company.city,
            organizationName: authoringCompanyName,
            closingDate: this.dateLocalPipe.transform(dueDate).toString(),
            creationDate: this.dateLocalPipe.transform(new Date()).toString(),
            creatorName: user.firstName + ' ' + user.lastName,
        };

        return {
            groupedcomments: groupedComments,
            closingentries: closingEntries,
            options,
            metadata: meta,
            generaldata,
        };
    }

    private collectAllComments(items: FcpTreeItem[], dueDate: Date, selection: ExportSelection): CommentDTO[] {
        let comments: CommentDTO[] = [];

        items.forEach((item) => {
            const filteredComments = item.comments
                /*.filter((comment) => {
                    if (selection.internalComments && comment.isInternal) return true;
                    if (selection.externalComments && comment.isExternal) return true;
                    if (selection.pendencies && comment.isPendency) return true;
                    if (selection.needsReview && comment.needsReview) return true;
                    return false;
                })*/
                .filter((comment) => new Date(comment.dueDate) >= dueDate);

            const mappedComments = filteredComments.map((comment) => ({
                dueDate: this.dateLocalPipe.transform(comment.dueDate).toString(),
                creationDate: this.dateLocalPipe.transform(comment.createdAt).toString(),
                text: comment.text,
                createdByName: comment.createdByName,
                isPendency: comment.isPendency,
                isInternal: comment.isInternal,
                isExternal: comment.isExternal,
                needsReview: comment.needsReview,
            }));

            comments = comments.concat(mappedComments);

            if (item.children && item.children.length > 0) {
                comments = comments.concat(this.collectAllComments(item.children, dueDate, selection));
            }
        });

        return comments;
    }

    // Updated method to group comments by the item's name
    private groupCommentsByItemName(comments: CommentDTO[], items: FcpTreeItem[]): GroupedCommentDTO[] {
        const groupedComments: { [itemName: string]: CommentDTO[] } = {};

        items.forEach((item) => {
            const itemComments = comments.filter((comment) =>
                item.comments.some((itemComment) => itemComment.text === comment.text),
            );

            if (itemComments.length > 0) {
                if (!groupedComments[item.name]) {
                    groupedComments[item.name] = [];
                }
                groupedComments[item.name].push(...itemComments);
            }

            // Recursively group for children
            if (item.children && item.children.length > 0) {
                const childGroupedComments = this.groupCommentsByItemName(comments, item.children);
                childGroupedComments.forEach((group) => {
                    if (!groupedComments[group.group]) {
                        groupedComments[group.group] = [];
                    }
                    groupedComments[group.group].push(...group.comments);
                });
            }
        });

        return Object.keys(groupedComments).map((group) => ({
            group,
            comments: groupedComments[group],
        }));
    }

    private collectClosingEntries(items: FcpTreeItem[], dueDate: Date): ClosingEntryDTO[] {
        let closingEntries: ClosingEntryDTO[] = [];

        items.forEach((item) => {
            const itemClosingEntries = item.closingEntries
                .filter((entry) => new Date(entry.dueDate) >= dueDate)
                .map((entry) => ({
                    dueDate: this.dateLocalPipe.transform(entry.dueDate).toString(),
                    text: entry.text,
                    amount: entry.amount,
                    debit: entry.debit,
                    debitName: entry.debitName,
                    credit: entry.credit,
                    creditName: entry.creditName,
                    booked: entry.booked,
                }));

            closingEntries = closingEntries.concat(itemClosingEntries);

            if (item.children && item.children.length > 0) {
                closingEntries = closingEntries.concat(this.collectClosingEntries(item.children, dueDate));
            }
        });

        return closingEntries;
    }
}
